/*
 * Created Date: Thu, 18th Feb 2021, 11:46:51 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2021 The Distance
 */

import buildGraphqlProvider, { buildQuery } from 'ra-data-graphql-simple';
import jsonRestProvider from 'ra-data-fakerest';
// custom queries/overrides
import {
  overrideQueries,
  overrideDataprovider,
  decorateResponse,
} from './overrides';
import getApolloClient from './client';
// schema, resourcemap, fakedata
import { schema, fakeData, resources } from './util';

const customBuildQuery = (introspection) => (type, resource, params) => {
  const builtQuery = buildQuery(introspection)(type, resource, params);
  const overriddenQuery = overrideQueries({
    type,
    resource,
    params,
    builtQuery,
  });
  if (overriddenQuery) {
    return {
      // Use the default query variables and parseResponse
      ...builtQuery,
      // Override the query
      query: overriddenQuery,
    };
  }

  return builtQuery;
};

const createProvider = async () => {
  const client = await getApolloClient(); // add options to client: {clientOptions, inMemCacheOptions}

  const gqlProvider = await buildGraphqlProvider({
    client,
    buildQuery: customBuildQuery,
    introspection: { schema },
  });

  const dataProviders = [
    // real data provider
    {
      dataProvider: gqlProvider,
      resources,
    },
    {
      dataProvider: jsonRestProvider(fakeData, true),
      //add resources here to override with fake data
      resources: {
        // evidence: 'Evidence',
        // plot: 'Plot',
        // plotSuspect: 'PlotSuspect',
        // plotEvidence: 'PlotEvidence',
        // event: 'Event',
        // eventSleuth: 'EventSleuth',
      },
    },
  ];

  return async (type, resource, params) => {
    const dataProviderMapping = dataProviders.find(
      (dp) => !dp.resources || dp.resources.hasOwnProperty(resource)
    );

    if (dataProviderMapping.resources) {
      resource = dataProviderMapping.resources[resource];
    }

    const customDataProvider = await overrideDataprovider({
      type,
      resource,
      params,
      client,
    });

    if (customDataProvider) {
      return customDataProvider;
    }

    // Fakedata Dataprovider type mapping - remove once dev cycle/backend hook-up complete
    if (typeof dataProviderMapping.dataProvider === 'object') {
      switch (type) {
        case 'GET_LIST':
          type = 'getList';
          break;
        case 'GET_ONE':
          type = 'getOne';
          break;
        case 'GET_MANY':
          type = 'getMany';
          break;
        case 'GET_MANY_REFERENCE':
          type = 'getManyReference';
          break;
        case 'UPDATE':
          type = 'update';
          break;
        case 'UPDATE_MANY':
          type = 'updateMany';
          break;
        case 'CREATE':
          type = 'create';
          break;
        case 'DELETE':
          type = 'delete';
          break;
        case 'DELETE_MANY':
          type = 'deleteMany';
          break;
        default:
          throw new Error('Unsupported query type');
      }

      const result = await dataProviderMapping.dataProvider[type](
        resource,
        params
      );

      return decorateResponse({
        type,
        resource,
        result,
      });
    }

    // catch any errors from apollo-client and wrap them to remove GraphQL error pre-pend
    try {
      const result = await dataProviderMapping.dataProvider(
        type,
        resource,
        params
      );
      return decorateResponse({
        type,
        resource,
        result,
      });
    } catch (e) {
      if (e.graphQLErrors && e.graphQLErrors.length) {
        const [error] = e.graphQLErrors;
        const message = error.message;
        throw new Error(message);
      }
      throw e;
    }
  };
};

let provider = null;
const getOrCreateProvider = async () => {
  if (!provider) {
    provider = await createProvider();
  }

  return provider;
};

export default getOrCreateProvider;
